import { NgClass } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { afterNextRender, ChangeDetectionStrategy, Component, inject, Injector, Input, signal } from '@angular/core';
import type { Subscription } from 'rxjs';

import { AbstractWebsiteComponent } from 'components/molecules/abstract-website-component/abstract-website-component';
import type { MenuItem } from 'components/organisms/sidebar/entities/types';
import { SidebarService } from 'components/organisms/sidebar/sidebar.service';
import { LinkTargetDetectorDirective } from 'src/directives/link-target-detector/link-target-detector.directive';
import { AnchorsService } from 'src/services/anchors/anchors.service';
import { LocaleService } from 'src/services/locale/locale.service';
import { UrlService } from 'src/services/url/url.service';

@Component({
    selector: 'website-menu-item',
    templateUrl: './menu-item.component.html',
    styleUrls: ['./menu-item.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgClass, LinkTargetDetectorDirective],
})
export class MenuItemComponent extends AbstractWebsiteComponent implements OnInit, OnDestroy {
    public readonly COMPONENT_NAME = 'menu-item';

    public readonly injector = inject(Injector);
    private readonly sidebarService = inject(SidebarService);
    private readonly localeService = inject(LocaleService);
    private readonly urlService = inject(UrlService);
    public readonly anchorsService = inject(AnchorsService);

    @Input() public isFAQItem: boolean;
    @Input() public item: MenuItem;
    @Input() public parentItem?: MenuItem;
    @Input() public level = 1;

    public hasChildren: boolean;
    public isOpen = signal<boolean>(false);
    public isActiveAnchor = signal<boolean>(false);
    public url: string = null;
    public isCurrentMenu: boolean;

    private fragmentSubscription: Subscription;

    ngOnInit() {
        this.hasChildren = this.item?.children && this.item.children.length > 0;
        this.isCurrentMenu = this.item.url === this.urlService.getSecondUrlSegment();

        if (this.isCurrentMenu) {
            this.isOpen.set(true);
        }

        this.initLinkUrl();

        // Track active anchor by current URL fragment
        this.fragmentSubscription = this.anchorsService.fragment$.subscribe((fragment) => {
            if (this.item?.isAnchor) {
                this.isActiveAnchor.set(this.item.url === fragment);
            }
        });
    }

    ngOnDestroy(): void {
        this.fragmentSubscription?.unsubscribe();
    }

    public toggleOpen() {
        this.isOpen.set(!this.isOpen());
    }

    public anchorClick(url: string): void {
        this.sidebarService.hideMobileMenu();

        /** afterNextRender and requestAnimationFrame are needed to make sure
         * the scroll happens after the mobile menu is hidden and its height is updated */

        afterNextRender(
            () => {
                requestAnimationFrame(() => {
                    this.anchorsService.clickOnAnchor(url);
                });
            },
            { injector: this.injector }
        );
    }

    private initLinkUrl() {
        const locale = this.localeService.getPathToLocaleSlug();
        const firstUrlSegment = this.urlService.getFirstUrlSegment();
        const pathname = `${locale}/${firstUrlSegment}`;
        const isSubitemAnchorLink = this.level > 1 && !this.item.isAnchor;

        if (isSubitemAnchorLink && !this.isFAQItem) {
            const anchorPart = `${this.parentItem.url}/#${this.item.url}`;

            this.url = `${pathname}/${anchorPart}`;
        } else {
            this.url = `${pathname}/${this.item.url ? this.item.url + '/' : ''}`;
        }
    }
}
